/**
 * Generates a curl command string from structured HTTP request fields.
 */
export function structuredToCurl({ method, url, headers, queryParams, body, bodyType, authType, auth }) {
  const parts = ['curl'];

  if (method && method !== 'GET') {
    parts.push(`-X ${method}`);
  }

  // Build full URL with query params
  let fullUrl = url || '';
  const activeParams = (queryParams || []).filter((p) => p.enabled !== false && p.key?.trim());
  if (activeParams.length > 0) {
    const qs = activeParams.map((p) => `${encodeURIComponent(p.key)}=${encodeURIComponent(p.value || '')}`).join('&');
    fullUrl += (fullUrl.includes('?') ? '&' : '?') + qs;
  }
  parts.push(`"${fullUrl}"`);

  // Collect headers (including auth-generated ones)
  const allHeaders = [];

  if (authType === 'bearer' && auth?.token) {
    allHeaders.push({ key: 'Authorization', value: `Bearer ${auth.token}` });
  } else if (authType === 'basic' && (auth?.username || auth?.password)) {
    const encoded = btoa(`${auth.username || ''}:${auth.password || ''}`);
    allHeaders.push({ key: 'Authorization', value: `Basic ${encoded}` });
  } else if (authType === 'api-key' && auth?.apiKeyName && auth?.apiKeyLocation === 'header') {
    allHeaders.push({ key: auth.apiKeyName, value: auth.apiKeyValue || '' });
  }

  const activeHeaders = (headers || []).filter((h) => h.enabled !== false && h.key?.trim());
  allHeaders.push(...activeHeaders);

  for (const h of allHeaders) {
    parts.push(`-H "${h.key}: ${(h.value || '').replace(/"/g, '\\"')}"`);
  }

  // Body
  if (body && bodyType && bodyType !== 'none') {
    const escapedBody = body.replace(/"/g, '\\"');
    parts.push(`-d "${escapedBody}"`);
  }

  return parts.join(' \\\n  ');
}

/**
 * Parses query parameters from a URL string.
 * Returns { baseUrl, params: [{key, value, enabled}] }
 */
export function parseUrlParams(url) {
  if (!url || !url.includes('?')) {
    return { baseUrl: url || '', params: [] };
  }
  const qIdx = url.indexOf('?');
  const baseUrl = url.substring(0, qIdx);
  const queryString = url.substring(qIdx + 1);
  if (!queryString.trim()) {
    return { baseUrl, params: [] };
  }
  const params = queryString.split('&').map((segment) => {
    const eqIdx = segment.indexOf('=');
    if (eqIdx === -1) {
      return { key: segment, value: '', enabled: true };
    }
    return {
      key: decodeURIComponent(segment.substring(0, eqIdx)),
      value: decodeURIComponent(segment.substring(eqIdx + 1)),
      enabled: true,
    };
  });
  return { baseUrl, params };
}

/**
 * Builds a full URL from a base URL and query param rows.
 */
export function buildUrlWithParams(baseUrl, queryParams) {
  const active = (queryParams || []).filter((p) => p.enabled !== false && p.key?.trim());
  if (active.length === 0) return baseUrl || '';
  const qs = active.map((p) => `${encodeURIComponent(p.key)}=${encodeURIComponent(p.value || '')}`).join('&');
  return `${baseUrl || ''}?${qs}`;
}
